import { useParams, Link } from 'react-router-dom';
import PageMeta from '@/components/base/PageMeta';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getComicById, getRelatedComics } from '@/mocks/comics';
import NotFound from '@/pages/NotFound';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '');

  if (!comic) {
    return <NotFound />;
  }

  const relatedComics = getRelatedComics(comic);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画 | 尾巴漫画`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[0]?.date,
    dateModified: comic.updatedAt,
    image: comic.cover,
    publisher: {
      '@type': 'Organization',
      name: '尾巴漫画',
    },
  };

  return (
    <>
      <PageMeta
        title={`${comic.title} - 韩国漫画在线阅读 | 尾巴漫画`}
        description={comic.description.slice(0, 150)}
        keywords={`${comic.title},韩国漫画,${comic.tags.join(',')},无删减漫画,下拉式韩漫,尾巴漫画`}
        canonicalUrl={`/comic/${comic.id}`}
        ogType="article"
        ogImage={comic.cover}
        jsonLd={articleJsonLd}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '分类浏览', href: '/category/all' },
                { label: comic.category, href: `/category/${comic.categorySlug}` },
                { label: comic.title },
              ]}
            />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <article className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-[300px] shrink-0">
              <div className="rounded-lg overflow-hidden">
                <img
                  src={comic.cover}
                  alt={`${comic.title} - 韩国漫画封面`}
                  title={`${comic.title} - 韩国漫画`}
                  className="w-full object-cover object-top aspect-[3/4]"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                  comic.status === '连载中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-500 text-xs font-medium whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">{comic.title}</h1>
              <p className="text-sm text-foreground-500 mb-6">作者：{comic.author}</p>

              <div className="flex items-center gap-6 mb-6">
                <div className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-500 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-lg font-bold text-foreground-950">{comic.rating}</span>
                  <span className="text-xs text-foreground-500">/ 10</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <i className="ri-eye-line text-foreground-400 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-sm text-foreground-500">{comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)} 万次` : `${comic.views} 次`}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <i className="ri-calendar-line text-foreground-400 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-sm text-foreground-500">更新于 {comic.updatedAt}</span>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-5 mb-6">
                <h2 className="text-sm font-semibold text-foreground-950 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button className="flex-1 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-300 text-center whitespace-nowrap cursor-pointer">
                  开始阅读 第1话
                </button>
                <button className="flex-1 px-6 py-3 rounded-full bg-background-100 text-foreground-700 font-medium text-sm hover:bg-background-200 transition-all duration-300 text-center whitespace-nowrap border border-background-200/70 cursor-pointer">
                  加入收藏
                </button>
              </div>
            </div>
          </article>

          <section className="mt-12">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6">章节列表</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {comic.chapters.map((chapter) => (
                <button
                  key={chapter.id}
                  className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-background-200 border border-background-200/70 transition-all duration-200 text-left cursor-pointer group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xs text-foreground-400 font-mono whitespace-nowrap">第{chapter.number}话</span>
                    <span className="text-sm text-foreground-700 font-medium truncate group-hover:text-primary-500 transition-colors duration-200">
                      {chapter.title}
                    </span>
                  </div>
                  <span className="text-xs text-foreground-400 whitespace-nowrap ml-2">{chapter.date}</span>
                </button>
              ))}
            </div>
          </section>

          {relatedComics.length > 0 && (
            <section className="mt-14">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}